import { Box, debounce, LinearProgress } from "@mui/material";
import { styled } from "@mui/system";
import { m, Transition, useMotionValue } from "framer-motion";
import { RefObject, useContext, useEffect, useRef, useState } from "react";

import { useGlTransition } from "./hooks/useGlTransition";
import { Visualization2DState, Visualization2DProps } from "./Visualization2DTypes";
import { requestCORSIfNotSameOrigin } from "./utils/requestCORSIfNotSameOrigin";
import { getTurnFrame } from "./utils/getTurnFrame";
import { DEMO } from "./utils/createGL";
import { StreamContext } from "contexts/Stream";
import { DYNAMIC_BASE_URL } from "shared/enum";

enum LOADING {
  NOT_LOADED = 0,
  POSTER_LOADED = 1,
  TURN_LOADING = 2,
  TURN_LOADING_COMPLETED = 3,
  ERROR = 4,
}

export default function Visualization2D(props: Visualization2DProps) {
  const {
    isMobile,
    transition,
    inTransition,
    src,
    reverse,
    selectedEnv,
    handleFrameChange,
    onLoad,
    turnFrame,
    turntableIsEnabled,
    onMouseDown,
    dayMode,
    drawerOpen,
    handleNotLoaded,
  } = props;
  const canvasRef: RefObject<HTMLCanvasElement> = useRef(null);
  const canvasContainerRef: RefObject<HTMLDivElement> = useRef(null);
  const abortControllers = useRef<AbortController[]>([]);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const setGlTransition = useGlTransition(canvasRef, transition, reverse);
  const [mode, setMode] = useState(dayMode);

  const [state, setState] = useState<Visualization2DState>({
    fitHorizontally: true,
    mouseDown: false,
    startX: x.get(),
    startPageX: 0,
    startY: y.get(),
    startPageY: 0,
    dragStartFrame: 0,
    turnFrame,
    loadingStatus: LOADING.NOT_LOADED,
    turnImages: [],
    loadedItems: 0,
  });

  const { turntableState } = useContext(StreamContext);

  const { turntableCameraNamePrefix } = turntableState;

  useEffect(() => {
    if (state.loadingStatus === LOADING.NOT_LOADED && !drawerOpen) {
      handleNotLoaded(true);
    } else if (
      state.loadingStatus === LOADING.TURN_LOADING ||
      state.loadingStatus === LOADING.POSTER_LOADED
    ) {
      handleNotLoaded(false, 300);
    }
  }, [state.loadingStatus]);

  const getPrefix = () => {
    switch (selectedEnv) {
      case 1:
        return "";
      case 2:
        return "beach_";
      case 3:
        return "beach_";
      case 4:
        return "beach_2_";
      case 5:
        return "residence_";
      default:
        return "";
    }
  };

  useEffect(() => {
    const debounced = debounce(() => resizeCanvas(), 20);
    window.addEventListener(`resize`, debounced);

    return () => {
      window.removeEventListener(`resize`, debounced);
      onLoad(undefined);
    };
  }, []);

  const replaceCameraInSrc = (newCamera: string, _src: string): string => {
    const url = new URL(_src);
    url.searchParams.set("camera", newCamera);
    return url.href;
  };

  const loadTurnPoster = (scr: string) => {
    setState((s) => ({ ...s, loadingStatus: LOADING.NOT_LOADED, loadedItems: 0, turnImages: [] }));
    const posterImage = new Image();

    const path = replaceCameraInSrc(`${getPrefix()}${turntableCameraNamePrefix}${turnFrame}`, src);

    requestCORSIfNotSameOrigin(posterImage, path);
    posterImage.onload = () => {
      setState((s) => ({ ...s, loadingStatus: LOADING.POSTER_LOADED }));
      setGlTransition(transition, reverse, posterImage, {
        duration: 0.5,
      });
      // preloadFrames(src);
    };
    posterImage.onerror = () => console.warn("Poster image could not be loaded");
    posterImage.src = path;
  };

  const preloadFrames = (scr: string) => {
    setState((s) => ({ ...s, loadingStatus: LOADING.NOT_LOADED, loadedItems: 0, turnImages: [] }));
    const newTurnImages: { image: HTMLImageElement; path: string; frameIndex: number }[] = [];

    // Rough to fine loading
    const unsorted = Array.from({ length: DEMO.TURNTABLE.TOTAL_FRAMES }, (value, index) => index);
    const roughSteps1 = unsorted.filter((_, index) => index % 8 === 0);
    const roughSteps2 = unsorted.filter((_, index) => (index + 4) % 8 === 0);
    const roughSteps3 = unsorted.filter((_, index) => (index + 2) % 4 === 0);
    const fineSteps = unsorted.filter((_, index) => (index + 1) % 2 === 0);

    const order = [...roughSteps1, ...roughSteps2, ...roughSteps3, ...fineSteps];

    const loadingQueue = order.map((index) => ({
      frameIndex: index,
      path: replaceCameraInSrc(`${getPrefix()}${turntableCameraNamePrefix}${index}`, src),
    }));

    loadingQueue.forEach((item: { frameIndex: number; path: string }) => {
      loadFrameImage(item, newTurnImages);
    });
  };

  const loadFrameImage = (
    item: { frameIndex: number; path: string },
    newTurnImages: { image: HTMLImageElement; path: string; frameIndex: number }[],
  ) => {
    const controller = new AbortController();
    const signal = controller.signal;

    abortControllers.current[item.frameIndex] = controller;

    const image = new Image();
    requestCORSIfNotSameOrigin(image, item.path);
    image.onload = () => {
      newTurnImages[item.frameIndex] = { image, path: item.path, frameIndex: item.frameIndex };

      setState((s) => ({
        ...s,
        turnImages: newTurnImages,
        loadedItems: newTurnImages.flat().length,
        loadingStatus:
          s.loadedItems + 1 === DEMO.TURNTABLE.TOTAL_FRAMES
            ? LOADING.TURN_LOADING_COMPLETED
            : LOADING.TURN_LOADING,
      }));
    };

    image.onerror = () => console.warn(`Frame ${item.frameIndex} could not be loaded.`);

    signal.addEventListener("abort", () => {
      image.src = "";
    });

    image.src = item.path;
  };

  useEffect(() => {
    // Load poster image
    loadTurnPoster(src);

    // Display the current frame immediately after loading the poster
    if (state.turnImages[turnFrame]?.image) {
      setGlTransition(transition, reverse, state.turnImages[turnFrame].image, {
        duration: 0.5,
      });
    }
  }, [src]);

  // useEffect(() => {
  //   // load poster image
  //   if (isMobile) {
  //     preloadFrames(src);
  //     loadTurnPoster(src);
  //   } else {
  //     loadTurnPoster(src);
  //   }
  // }, [src, isMobile]);

  useEffect(() => {
    // load poster image
    if (isMobile || turntableCameraNamePrefix) {
      if (!drawerOpen) {
        preloadFrames(src);
      }
      loadTurnPoster(src);
    } else {
      loadTurnPoster(src);
    }
  }, [src, isMobile, turntableCameraNamePrefix]);

  const animateTurnImage = (duration: number) => {
    if (state.turnImages[turnFrame]?.image) {
      setGlTransition(transition, reverse, state.turnImages[turnFrame].image, {
        duration,
      });
    } else {
      //  find nearest image
      const indexesOfLoadedImages = state.turnImages
        .map((image, i) => (image.image ? i : false))
        .flat();
      if (indexesOfLoadedImages.length) {
        const closest = indexesOfLoadedImages.reduce((prev, curr) =>
          Math.abs(Number(curr) - turnFrame) < Math.abs(Number(prev) - turnFrame) ? curr : prev,
        );
        if (closest) {
          setGlTransition(transition, reverse, state.turnImages[closest].image, {
            duration: 0.5,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (turntableIsEnabled) {
      animateTurnImage(0);
    }
  }, [turnFrame]);

  useEffect(() => {
    if (turntableIsEnabled && state.loadingStatus !== LOADING.TURN_LOADING_COMPLETED) {
      abortControllers.current.forEach((controller) => controller && controller.abort());
      preloadFrames(src);
    }
  }, [turntableIsEnabled]);

  useEffect(() => {
    if (canvasRef.current) {
      onLoad(canvasRef.current);
    }
  }, [canvasRef]);

  useEffect(() => {
    if (inTransition === true) {
      resizeCanvas();
    }
  }, [inTransition]);

  const resizeCanvas = () => {
    if (canvasContainerRef.current) {
      const { width, height } = canvasContainerRef.current.getBoundingClientRect();
      const inputRatio = DEMO.RENDER_SIZE_WIDTH / DEMO.RENDER_SIZE_HEIGHT;
      const canvasRatio = width / height;
      const fitHorizontally = inputRatio < canvasRatio;
      setState((s) => ({ ...s, fitHorizontally }));
    }
  };

  useEffect(() => {
    setToBoundaries();
  }, [state.fitHorizontally]);

  const setToBoundaries = () => {
    if (canvasContainerRef.current && canvasRef.current) {
      const containerRect = canvasContainerRef.current?.getBoundingClientRect();
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      const boundaryTransition: Transition = {
        ease: "easeInOut",
        duration: 0.3,
      };

      if (canvasRect.top > containerRect.top && inTransition) {
        // animate(y, (canvasRect.height - containerRect.height) / 2, boundaryTransition);
      } else if (canvasRect.bottom < containerRect.bottom) {
        // animate(y, -(canvasRect.height - containerRect.height) / 2, boundaryTransition);
      }

      if (canvasRect.left > containerRect.left && inTransition) {
        // animate(x, (canvasRect.width - containerRect.width) / 2, boundaryTransition);
      } else if (canvasRect.right < containerRect.right) {
        // animate(x, -(canvasRect.width - containerRect.width) / 2, boundaryTransition);
      }
    }
  };

  if (mode !== dayMode) {
    abortControllers.current.forEach((controller) => controller && controller.abort());
    preloadFrames(src);
    setMode(dayMode);
    setState((prev) => ({ ...prev, loadingStatus: LOADING.NOT_LOADED }));
  }

  // const handleMouseCancel = () => {
  //   setToBoundaries();
  //   setState((s) => ({ ...s, mouseDown: false }));
  // };

  // const handleMouseMove = (e: { pageX: number; pageY: number }) => {
  //   if (state.mouseDown && canvasContainerRef.current && canvasRef.current) {
  //     const moveDistanceX = e.pageX - state.startPageX;

  //     if (turntableIsEnabled) {
  //       const newFrame = getTurnFrame(moveDistanceX, state.dragStartFrame);
  //       if (newFrame !== state.turnFrame) {
  //         setState((s) => ({
  //           ...s,
  //           turnFrame: newFrame,
  //         }));

  //         handleFrameChange(newFrame);
  //       }
  //     } else {
  //       const moveDistanceY = e.pageY - state.startPageY;
  //       const damping = 0.5;
  //       x.set(state.startX + moveDistanceX * damping);
  //       y.set(state.startY + moveDistanceY * damping);
  //     }
  //   }
  // };

  // const handleMouseDown = (e: { pageX: number; pageY: number }) => {
  //   setState((s) => ({
  //     ...s,
  //     mouseDown: true,
  //     startX: x.get(),
  //     startPageX: e.pageX,
  //     startY: y.get(),
  //     startPageY: e.pageY,
  //     dragStartFrame: s.turnFrame,
  //   }));
  // };

  const handleCancel = () => {
    setToBoundaries();
    setState((s) => ({ ...s, mouseDown: false }));
  };

  const handleMove = (pageX: number, pageY: number) => {
    if (state.mouseDown && canvasContainerRef.current && canvasRef.current) {
      const moveDistanceX = pageX - state.startPageX;

      if (turntableIsEnabled) {
        const newFrame = getTurnFrame(moveDistanceX, state.dragStartFrame);
        if (newFrame !== state.turnFrame) {
          setState((s) => ({
            ...s,
            turnFrame: newFrame,
          }));

          handleFrameChange(newFrame);
        }
      } else {
        const moveDistanceY = pageY - state.startPageY;
        const damping = 0.5;
        // x.set(state.startX + moveDistanceX * damping);
        // y.set(state.startY + moveDistanceY * damping);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => handleMove(e.pageX, e.pageY);

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      handleMove(touch.pageX, touch.pageY);
    }
  };

  const handleDown = (pageX: number, pageY: number) => {
    setState((s) => ({
      ...s,
      mouseDown: true,
      startX: x.get(),
      startPageX: pageX,
      startY: y.get(),
      startPageY: pageY,
      dragStartFrame: s.turnFrame,
    }));
  };

  const handleMouseDown = (e: React.MouseEvent) => handleDown(e.pageX, e.pageY);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      handleDown(touch.pageX, touch.pageY);
    }
  };

  const cursor = () => {
    return `url(${DYNAMIC_BASE_URL}/resources/turn.png) 24 24, auto`;
  };

  const loadingPercentage = Math.round((100 / DEMO.TURNTABLE.TOTAL_FRAMES) * state.loadedItems);

  return (
    <ContainerCanvas ref={canvasContainerRef} onMouseDown={onMouseDown}>
      <Loading
        variants={LoadingVariants}
        // initial="hidden"
        // animate={
        //   state.loadingStatus === LOADING.TURN_LOADING &&
        //   turntableIsEnabled &&
        //   loadingPercentage !== 100
        //     ? "visible"
        //     : "hidden"
        // }
        style={{
          opacity:
            state.loadingStatus === LOADING.TURN_LOADING &&
            turntableIsEnabled &&
            loadingPercentage !== 100
              ? 1
              : 0,
          transition: `opacity 1s`,
        }}
      >
        {loadingPercentage}%
      </Loading>

      <GLCanvas
        onMouseDown={handleMouseDown}
        onMouseUp={handleCancel}
        onMouseLeave={handleCancel}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleCancel}
        onTouchCancel={handleCancel}
        initial={{
          width: state.fitHorizontally ? "100%" : "auto",
          height: "100%",
        }}
        animate={{
          width: state.fitHorizontally ? "100%" : "auto",
          height: "100%",
        }}
        transition={DEMO.TRANSITIONS.FAST}
        style={{
          x: x,
          padding: 0,
          display: "block",
          cursor: cursor(),
          objectFit: "cover",
        }}
        ref={canvasRef}
      />
      {state.loadingStatus === LOADING.NOT_LOADED && (
        <Box sx={{ width: "100%", position: "fixed", top: 0, left: 0 }}>
          <LinearProgress />
        </Box>
      )}
    </ContainerCanvas>
  );
}

const GLCanvas = styled(m.canvas)({
  padding: 0,
  margin: 0,
  border: 0,
  outline: 0,
  position: "relative",
});

const ContainerCanvas = styled("div")(({ theme }) => ({
  width: "100%",
  justifyContent: "center",
  alignItems: "center",
  display: "flex",
  overflow: "hidden",
  gridColumnStart: 1,
  gridColumnEnd: -2,
  gridRowStart: 1,
  gridRowEnd: -1,
  [theme.breakpoints.down("md")]: {
    gridColumnEnd: -1,
    gridRowEnd: -2,
  },
}));

const Loading = styled(m.p)({
  position: "absolute",
  bottom: "100px",
  left: "50%",
  zIndex: 10000,
  transform: "translateX(-50%)",
  pointerEvents: "none",
  textShadow: "0 0 5px #000",
  fontSize: "1.5em",
  color: "white",
});

const LoadingVariants = {
  visible: { opacity: 1, bottom: "110px", transition: { delay: 0.8 } },
  hidden: { opacity: 0, bottom: "90px" },
};
